/**
 * GlobalKeyboardShortcuts Component
 *
 * Provides global keyboard shortcuts for the entire application.
 * Should be mounted once at the app root level.
 *
 * Global Shortcuts:
 * - Cmd/Ctrl+K: Open command palette / search
 * - ?: Open keyboard shortcuts help
 * - Esc: Close modals/dialogs
 * - Alt+1-5: Navigate to key sections
 * - Cmd/Ctrl+[/]: Browser history navigation
 * - Cmd/Ctrl+Shift+R: Refresh page data
 *
 * Convoy-050 gt-ux017 — Keyboard Shortcuts Alignment
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { KeyboardShortcutsHelp } from './KeyboardShortcutsHelp';

export interface GlobalKeyboardShortcutsProps {
  /** Whether to enable global shortcuts */
  enabled?: boolean;
  /** Handler to toggle the command palette */
  onToggleCommandPalette?: () => void;
  /** Custom navigation shortcuts */
  customShortcuts?: Array<{
    key: string;
    path: string;
    description: string;
    number?: number;
  }>;
}

export const GlobalKeyboardShortcuts: React.FC<GlobalKeyboardShortcutsProps> = ({
  enabled = true,
  onToggleCommandPalette,
  customShortcuts = [],
}) => {
  const navigate = useNavigate();
  const { registerShortcut } = useKeyboardShortcuts({ enabled });
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const shortcuts = [
      // Help — Shift+?
      {
        key: '?',
        shift: true,
        description: 'Show keyboard shortcuts help',
        handler: () => setShowHelp(true),
        context: 'global',
        group: 'Global',
      },

      // Navigation shortcuts (Alt+Number)
      {
        key: '1',
        alt: true,
        description: 'Go to Dashboard',
        handler: () => navigate('/dashboard'),
        context: 'global',
        group: 'Navigation',
      },
      {
        key: '2',
        alt: true,
        description: 'Go to Entities',
        handler: () => navigate('/entities'),
        context: 'global',
        group: 'Navigation',
      },
      {
        key: '3',
        alt: true,
        description: 'Go to Documents',
        handler: () => navigate('/documents'),
        context: 'global',
        group: 'Navigation',
      },
      {
        key: '4',
        alt: true,
        description: 'Go to Compliance',
        handler: () => navigate('/compliance'),
        context: 'global',
        group: 'Navigation',
      },
      {
        key: '5',
        alt: true,
        description: 'Go to Settings',
        handler: () => navigate('/settings'),
        context: 'global',
        group: 'Navigation',
      },

      // Browser history navigation
      {
        key: '[',
        ctrl: true,
        description: 'Go back in history',
        handler: () => navigate(-1),
        context: 'global',
        group: 'Navigation',
      },
      {
        key: ']',
        ctrl: true,
        description: 'Go forward in history',
        handler: () => navigate(1),
        context: 'global',
        group: 'Navigation',
      },

      // Refresh data (Cmd/Ctrl+Shift+R)
      {
        key: 'r',
        ctrl: true,
        shift: true,
        description: 'Refresh page data',
        handler: () => {
          window.dispatchEvent(new CustomEvent('refresh-data'));
        },
        context: 'global',
        group: 'Global',
      },
    ];

    // Add custom shortcuts
    const customShortcutMappings = customShortcuts.map((shortcut) => ({
      key: shortcut.number?.toString() || shortcut.key,
      alt: !!shortcut.number,
      description: shortcut.description,
      handler: () => navigate(shortcut.path),
      context: 'global',
      group: 'Navigation',
    }));

    // Register all shortcuts
    [...shortcuts, ...customShortcutMappings].map((shortcut) =>
      registerShortcut(shortcut)
    );

    // Cleanup handled by hook
    return () => {};
  }, [enabled, navigate, registerShortcut, customShortcuts]);

  // Cmd/Ctrl+K — separate listener so it works even when typing in inputs
  useEffect(() => {
    if (!enabled) return;

    const handleCmdK = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        e.stopPropagation();
        onToggleCommandPalette?.();
      }
    };

    // Use capture phase so this fires before input-level handlers
    window.addEventListener('keydown', handleCmdK, true);
    return () => window.removeEventListener('keydown', handleCmdK, true);
  }, [enabled, onToggleCommandPalette]);

  return (
    <>
      <KeyboardShortcutsHelp
        defaultOpen={showHelp}
        onOpenChange={setShowHelp}
      />
    </>
  );
};

/**
 * Context-specific keyboard shortcuts for different pages
 */
export interface PageKeyboardShortcutsProps {
  /** Page context (should match route path) */
  context: string;
  /** Shortcuts specific to this page */
  shortcuts: Array<{
    key: string;
    ctrl?: boolean;
    alt?: boolean;
    shift?: boolean;
    description: string;
    handler: () => void;
    group?: string;
  }>;
}

export const PageKeyboardShortcuts: React.FC<PageKeyboardShortcutsProps> = ({
  context,
  shortcuts,
}) => {
  const { registerShortcut } = useKeyboardShortcuts();

  useEffect(() => {
    const shortcutIds = shortcuts.map((shortcut) =>
      registerShortcut({
        ...shortcut,
        context,
        group: shortcut.group || 'Page Actions',
      })
    );

    return () => {
      // Shortcuts are automatically cleaned up by the hook
    };
  }, [context, shortcuts, registerShortcut]);

  return null;
};

export default GlobalKeyboardShortcuts;
